import { NodeResizer, useReactFlow } from '@xyflow/react';
import { cn } from '@/lib/utils';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useI18n } from '@/providers/i18n';
import type { StickyNoteNodeProps } from '.';

type StickyNotePrimitiveProps = StickyNoteNodeProps & {
  title: string;
};

const STICKY_COLORS = [
  { name: 'yellow', bg: 'bg-[#fef9c3]', border: 'border-yellow-200/50', text: 'text-yellow-900' },
  { name: 'blue', bg: 'bg-[#e0f2fe]', border: 'border-blue-200/50', text: 'text-blue-900' },
  { name: 'green', bg: 'bg-[#dcfce7]', border: 'border-green-200/50', text: 'text-green-900' },
  { name: 'pink', bg: 'bg-[#fce7f3]', border: 'border-pink-200/50', text: 'text-pink-900' },
  { name: 'purple', bg: 'bg-[#f3e8ff]', border: 'border-purple-200/50', text: 'text-purple-900' },
];

export const StickyNotePrimitive = ({
  data,
  id,
  selected,
}: StickyNotePrimitiveProps) => {
  const { t } = useI18n();
  const { updateNodeData } = useReactFlow();
  const [text, setText] = useState(data.text ?? '');
  const [isEditing, setIsEditing] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const color = STICKY_COLORS.find(c => c.name === data.color) || STICKY_COLORS[0];

  useEffect(() => {
    setText(data.text ?? '');
  }, [data.text]);

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newText = e.target.value;
    setText(newText);
    updateNodeData(id, { text: newText });
  };

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(true);
    setTimeout(() => {
      textareaRef.current?.focus();
    }, 0);
  };

  const handleBlur = () => {
    setIsEditing(false);
  };

  return (
    <div 
      onDoubleClick={handleDoubleClick}
      className={cn(
        "relative group w-full h-full min-w-[100px] min-h-[100px] rounded-3xl transition-shadow duration-200",
        color.bg,
        selected ? "shadow-none" : "shadow-none hover:shadow-none"
      )}
    >
      {selected && (
        <NodeResizer
          minWidth={100}
          minHeight={100}
          isVisible={selected}
          lineClassName="border-transparent"
          handleClassName="h-3 w-3 bg-white border-2 border-primary rounded-full"
        />
      )}
      
      <div className="w-full h-full p-4 flex flex-col">
        <textarea
          ref={textareaRef}
          value={text}
          onChange={handleTextChange}
          onBlur={handleBlur}
          readOnly={!isEditing}
          placeholder={t('Double-click to input note...')}
          className={cn(
            "nodrag nowheel w-full h-full bg-transparent border-none outline-none resize-none overflow-hidden text-base font-bold leading-relaxed placeholder:opacity-40",
            color.text,
            !isEditing && "pointer-events-none select-none"
          )}
        />
      </div>

      {/* Subtle color switcher on hover when selected */}
      {selected && !isEditing && (
        <div className="fm-node-bottom-toolbar-surface absolute -bottom-10 left-1/2 -translate-x-1/2 flex items-center gap-1.5 p-1.5 rounded-xl bg-sidebar/95 backdrop-blur-xl border border-sidebar-border shadow-none transition-all animate-in fade-in slide-in-from-top-2">
          {STICKY_COLORS.map((c) => (
            <button
              key={c.name}
              onClick={() => updateNodeData(id, { color: c.name })}
              className={cn(
                "size-5 rounded-full border transition-transform hover:scale-110 active:scale-95",
                c.bg,
                data.color === c.name || (!data.color && c.name === 'yellow') ? "border-primary ring-2 ring-primary/20" : "border-black/5"
              )}
              title={t(c.name)}
            />
          ))}
        </div>
      )}
    </div>
  );
};
